import { Dialog, Empty, ErrorState, Loading, PageHead, Select, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ComparePeople, type PersonSummary } from '../crm/ComparePeople';
import { Field } from '../crm/forms';
import { usePeopleOptions } from '../crm/data';
import { api } from '../lib/api';

interface Pair {
  score: number;
  reasons: string[];
  a: PersonSummary;
  b: PersonSummary;
}
type Side = PersonSummary;

/** Review queue for possible duplicates. Nothing is merged automatically; every merge can be undone. */
export function DuplicatesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['duplicates'],
    queryFn: () => api<{ items: Pair[] }>('/v1/people/duplicates'),
  });
  const [merge, setMerge] = useState<{ keep: Side; drop: Side; reasons: string[] } | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['duplicates'] });
    void qc.invalidateQueries({ queryKey: ['people'] });
  };
  const dismiss = useMutation({
    mutationFn: (p: Pair) =>
      api('/v1/people/duplicates/dismiss', {
        method: 'POST',
        body: JSON.stringify({ a: p.a.id, b: p.b.id }),
      }),
    onSuccess: refresh,
  });
  const doMerge = useMutation({
    mutationFn: (m: { keep: Side; drop: Side; reasons: string[] }) =>
      api<{ survivorId: string }>('/v1/people/merge', {
        method: 'POST',
        body: JSON.stringify({ survivorId: m.keep.id, mergedId: m.drop.id, reasons: m.reasons }),
      }),
    onSuccess: (r) => {
      refresh();
      setMerge(null);
      navigate(`/people/${r.survivorId}`);
    },
  });

  return (
    <>
      <PageHead
        eyebrow={t('nav.people')}
        title={t('contacts.duplicates.title')}
        subtitle={t('contacts.duplicates.subtitle')}
      />
      {q.isLoading && <Loading />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data?.items.length === 0 && (
        <section className="card">
          <Empty text={t('contacts.duplicates.none')} />
        </section>
      )}
      <div className="dup-list" data-testid="duplicate-list">
        {q.data?.items.map((p) => (
          <article key={`${p.a.id}-${p.b.id}`} className="card dup-card" data-testid="duplicate-pair">
            <div className="dup-reasons">
              <span className="badge">
                {t('contacts.duplicates.match', { score: Math.round(p.score * 100) })}
              </span>
              {p.reasons.map((r) => (
                <span key={r} className="chip">
                  {t(`contacts.duplicates.reason.${r}`)}
                </span>
              ))}
            </div>
            <ComparePeople
              a={p.a}
              b={p.b}
              onKeep={(keep, drop) => setMerge({ keep, drop, reasons: p.reasons })}
            />
            <div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => dismiss.mutate(p)}
                disabled={dismiss.isPending}
                data-testid="not-same"
              >
                <X size={14} />
                {t('contacts.duplicates.notSame')}
              </button>
            </div>
          </article>
        ))}
      </div>
      {merge && (
        <Dialog
          open
          title={t('contacts.duplicates.confirmTitle')}
          onClose={() => setMerge(null)}
          onSubmit={() => doMerge.mutate(merge)}
          testId="merge-dialog"
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setMerge(null)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-grad" disabled={doMerge.isPending}>
                {t('contacts.duplicates.merge')}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            {t('contacts.duplicates.confirmBody', {
              keep: merge.keep.displayName,
              drop: merge.drop.displayName,
            })}
          </p>
          <p className="faint" style={{ fontSize: 'var(--t-xs)' }}>
            {t('contacts.duplicates.undoHint')}
          </p>
          {doMerge.isError && <ErrorState error={doMerge.error} />}
        </Dialog>
      )}
    </>
  );
}

/** Merge history on a person's page, with Undo. */
export function MergeHistory({ personId }: { personId: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['merges', personId],
    queryFn: () =>
      api<{
        items: {
          id: string;
          survivorId: string;
          mergedId: string;
          mergedName: string;
          mergedAt: string;
          undoneAt: string | null;
          mergedBy: string | null;
        }[];
      }>(`/v1/people/${personId}/merges`),
  });
  const undo = useMutation({
    mutationFn: (id: string) => api(`/v1/people/merges/${id}/undo`, { method: 'POST' }),
    onSuccess: () => {
      for (const k of ['merges', 'person', 'people', 'duplicates', 'timeline'])
        void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  const items = q.data?.items.filter((m) => m.survivorId === personId) ?? [];
  if (!items.length) return null;
  return (
    <section className="card" aria-label={t('contacts.merges.title')} data-testid="merge-history">
      <div className="card-head">
        <h2>{t('contacts.merges.title')}</h2>
      </div>
      <ul className="list-plain">
        {items.map((m) => (
          <li key={m.id}>
            <div className="grow" style={{ minWidth: 0 }}>
              <span dir="auto">{t('contacts.merges.merged', { name: m.mergedName })}</span>
              <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                {fmt.dateTime(m.mergedAt)}
                {m.mergedBy ? ` · ${m.mergedBy}` : ''}
                {m.undoneAt ? ` · ${t('contacts.merges.undone')}` : ''}
              </div>
            </div>
            {!m.undoneAt && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => undo.mutate(m.id)}
                disabled={undo.isPending}
              >
                {t('contacts.merges.undo')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {undo.isError && <ErrorState error={undo.error} />}
    </section>
  );
}

/** "Merge with…" on a person's page: pick any other person, compare side by side, choose which record to keep. */
export function MergeWithDialog({ personId, onClose }: { personId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const people = usePeopleOptions();
  const [other, setOther] = useState('');
  const cmp = useQuery({
    queryKey: ['compare', personId, other],
    enabled: !!other,
    queryFn: () => api<{ a: PersonSummary; b: PersonSummary }>(`/v1/people/compare?a=${personId}&b=${other}`),
  });
  const merge = useMutation({
    mutationFn: (m: { keep: PersonSummary; drop: PersonSummary }) =>
      api<{ survivorId: string }>('/v1/people/merge', {
        method: 'POST',
        body: JSON.stringify({ survivorId: m.keep.id, mergedId: m.drop.id, reasons: ['manual'] }),
      }),
    onSuccess: (r) => {
      for (const k of ['person', 'people', 'duplicates', 'merges', 'people-options'])
        void qc.invalidateQueries({ queryKey: [k] });
      onClose();
      navigate(`/people/${r.survivorId}`);
    },
  });
  return (
    <Dialog open wide title={t('contacts.mergeWith.title')} onClose={onClose} testId="merge-with">
      <p className="faint" style={{ marginTop: 0, fontSize: 'var(--t-sm)' }}>
        {t('contacts.mergeWith.hint')}
      </p>
      <Field id="merge-other" label={t('contacts.mergeWith.other')}>
        <Select
          id="merge-other"
          value={other}
          onChange={setOther}
          placeholder={t('contacts.mergeWith.choose')}
          options={(people.data?.items ?? [])
            .filter((p) => p.id !== personId)
            .map((p) => ({ value: p.id, label: p.displayName, hint: p.accountName ?? undefined }))}
        />
      </Field>
      {cmp.isError && <ErrorState error={cmp.error} />}
      {cmp.data && (
        <ComparePeople
          a={cmp.data.a}
          b={cmp.data.b}
          busy={merge.isPending}
          onKeep={(keep, drop) => merge.mutate({ keep, drop })}
        />
      )}
      <p className="faint" style={{ fontSize: 'var(--t-xs)' }}>
        {t('contacts.duplicates.undoHint', { keep: t('contacts.mergeWith.kept') })}
      </p>
      {merge.isError && <ErrorState error={merge.error} />}
    </Dialog>
  );
}
